import Bullet from 'components/Bullet';
import { DefaultLoader, LoaderContainer } from 'components/Loader';
import { getConversationName } from 'components/messaging/utils';
import { FC, useCallback } from 'react';
import styled from 'styled-components';
import useSWR from 'swr';
import Conversation from 'types/Conversation';
import User from 'types/User';
import { getLocaleDate, getLocaleTime } from 'utils/time';
import CustomNavLink from '../profileNavigation/Link';

const Container = styled.div`
  height: 100%;
  display: grid;
  grid-template-rows: min-content auto;
`;

const HeadContainer = styled.h1`
  font-weight: 500;
  font-size: 1.5rem;
  padding: 0 0 1rem 1rem;
  border-bottom: 1px solid var(--gray-200);
`;

const Conversations = styled.div`
  overflow: hidden auto;
`;

const ConversationsBox = styled(CustomNavLink)`
  padding: 0;
  border-bottom: none;
  .active {
    border-bottom: none;
  }

  .c-box {
    padding: 15px;
    border-bottom: 1px solid var(--gray-200);
    text-align: left;
    width: 100%;

    .c-name {
      color: black;
      font-weight: 500;
      margin-bottom: 2px;
      text-overflow: ellipsis;
      overflow: hidden;
    }
    .c-last-message {
      display: flex;
      column-gap: 0.5rem;
      width: max-content;
      margin-bottom: 4px;
      font-size: 15px;
      text-overflow: ellipsis;
      width: 100%;
      overflow: hidden;
    }
    .c-date {
      font-size: 12px;
      color: var(--gray-500);
    }
  }

  &:hover {
    .c-box {
      background-color: var(--gray-100);
    }
  }
  &:active {
    .c-box {
      background-color: var(--primary-100);
      color: var(--primary-700);
    }
  }

  &.active {
    border: none;
    .c-box {
      background-color: var(--primary-50);
      color: var(--primary-700);
    }
  }
`;

const ConversationsList: FC = () => {
  const { data: user }: { data?: User } = useSWR('/auth/profile');
  const { data: conversations, error } = useSWR<Conversation[]>(
    '/messaging/mentor-conversations',
  );

  const getConversationDisplayedText = useCallback((c: Conversation) => {
    if (!c.messages.length) return 'Envoyez le premier message!';

    if (c.messages[0].filename) {
      if (c.messages[0].senderId === user?.id)
        return 'Vous avez partagé un fichier';
      return `${c.messages[0].sender?.firstName} a partagé un fichier`;
    }
    return `${
      c.messages[0].senderId === user?.id
        ? 'Vous'
        : c.messages[0].sender?.firstName
    } : ${c.messages[0].text}`;
  }, [user?.id]);

  return (
    <Container>
      <HeadContainer>Messages</HeadContainer>
      <Conversations>
        {conversations && !error ? (
          conversations.map((c) => (
            <ConversationsBox key={c.id} to={`${c.id}`}>
              <div className="c-box">
                <div className="c-name">
                  {getConversationName(c, user!.id || -1)}
                </div>
                <div className="c-last-message">
                  {c.messages[0] ? (
                    c.messages[0].seen ? null : (
                      <Bullet />
                    )
                  ) : (
                    <Bullet />
                  )}
                  {getConversationDisplayedText(c)}
                </div>
                <div className="c-date">
                  {!!c.messages.length &&
                    `${getLocaleDate(
                      c.messages[0].createdAt || '',
                    )} - ${getLocaleTime(c.messages[0].createdAt || '')}`}
                </div>
              </div>
            </ConversationsBox>
          ))
        ) : (
          <LoaderContainer>
            <DefaultLoader size={1.5} />
          </LoaderContainer>
        )}
      </Conversations>
    </Container>
  );
};

export default ConversationsList;
